/**
 * CorrespondenceInbox
 *
 * Gmail-inspired correspondence inbox with clean, modern design.
 * Emphasis on typography, whitespace, and subtle interactions.
 */

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Row, Table } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useCorrespondencePackageCreateMutation } from '@/services/api/correspondenceService';

import { useInboxColumns } from './columns';
import {
  CreatePackageButton,
  InboxEmptyState,
  InboxFiltersBar,
} from './components';
import { PACKAGE_TYPE_CONFIG } from './constants';
import type {
  CorrespondenceInboxProps,
  CorrespondencePackageType,
  PackageRowData,
} from './types';
import { useCorrespondenceInbox } from './useCorrespondenceInbox';

export const CorrespondenceInbox: React.FC<CorrespondenceInboxProps> = ({
  matterId,
}) => {
  const navigate = useNavigate();

  const {
    filteredPackages,
    isLoading,
    filters,
    setSearch,
    setPackageType,
    setStatus,
    clearFilters,
    hasActiveFilters,
    rowSelection,
    setRowSelection,
    deleteSelected,
    isDeleting,
  } = useCorrespondenceInbox(matterId);

  const [createPackage, { isLoading: isCreating }] =
    useCorrespondencePackageCreateMutation();

  const handleCreate = useCallback(
    async (type: CorrespondencePackageType) => {
      try {
        const result = await createPackage({
          matterId,
          createCorrespondencePackage: {
            package_type: type,
            with_defaults: true,
          },
        }).unwrap();
        toast.success(`${PACKAGE_TYPE_CONFIG[type].label} created`);
        navigate(`/matters/${matterId}/correspondence/${result.id}`);
      } catch {
        toast.error('Failed to create correspondence');
      }
    },
    [createPackage, matterId, navigate],
  );

  const handleRowClick = useCallback(
    (id: string) => navigate(`/matters/${matterId}/correspondence/${id}`),
    [matterId, navigate],
  );

  const columns = useInboxColumns({ onRowClick: handleRowClick });

  const table = useReactTable({
    data: filteredPackages,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: (row) => !row.original.is_transmitted,
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    getRowId: (row) => row.id,
  });

  const selectedCount = Object.keys(rowSelection).length;
  const selectableRows = useMemo(
    () => table.getFilteredRowModel().rows.filter((r) => r.getCanSelect()),
    [table],
  );
  const allSelectableSelected =
    selectableRows.length === selectedCount && selectedCount > 0;

  const toggleSelectAll = useCallback(() => {
    if (allSelectableSelected) {
      setRowSelection({});
    } else {
      const selection: Record<string, boolean> = {};
      selectableRows.forEach((row) => {
        selection[row.id] = true;
      });
      setRowSelection(selection);
    }
  }, [allSelectableSelected, selectableRows, setRowSelection]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-stone-200">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-xl font-semibold text-stone-900 tracking-tight">
                Correspondence
              </h1>
              <p className="text-sm text-stone-500 mt-0.5">
                Fax, email, and mail delivery
              </p>
            </div>
            <CreatePackageButton
              onSelect={handleCreate}
              isLoading={isCreating}
            />
          </div>

          <InboxFiltersBar
            filters={filters}
            onSearchChange={setSearch}
            onTypeChange={setPackageType}
            onStatusChange={setStatus}
            onClear={clearFilters}
            hasActiveFilters={hasActiveFilters}
            resultCount={filteredPackages.length}
          />
        </div>
      </header>

      {/* Selection toolbar */}
      <SelectionToolbar
        count={selectedCount}
        total={selectableRows.length}
        allSelected={allSelectableSelected}
        onToggleAll={toggleSelectAll}
        onDelete={deleteSelected}
        onCancel={() => setRowSelection({})}
        isDeleting={isDeleting}
      />

      {/* Content */}
      <main className="flex-1 overflow-auto">
        {isLoading && <LoadingSkeleton />}
        {!isLoading && filteredPackages.length === 0 && (
          <InboxEmptyState
            hasFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            onCreatePackage={handleCreate}
            isCreating={isCreating}
          />
        )}
        {!isLoading && filteredPackages.length > 0 && (
          <InboxList table={table} onRowClick={handleRowClick} />
        )}
      </main>
    </div>
  );
};

// ============================================================================
// Selection Toolbar
// ============================================================================

interface SelectionToolbarProps {
  count: number;
  total: number;
  allSelected: boolean;
  onToggleAll: () => void;
  onDelete: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  count,
  total,
  allSelected,
  onToggleAll,
  onDelete,
  onCancel,
  isDeleting,
}) => {
  if (count === 0) return null;

  return (
    <div className="flex-shrink-0 bg-stone-900 text-white px-4 py-2 flex items-center gap-4 animate-in slide-in-from-top-1 duration-150">
      <Checkbox
        checked={allSelected}
        onCheckedChange={onToggleAll}
        className="border-stone-500 data-[state=checked]:bg-white data-[state=checked]:text-stone-900"
      />
      <span className="text-sm">
        <span className="font-medium">{count}</span>
        <span className="text-stone-300"> of {total} selected</span>
      </span>

      <div className="flex-1" />

      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        disabled={isDeleting}
        className="text-white hover:bg-stone-800 h-8 gap-1.5"
      >
        <TrashIcon className="h-4 w-4" />
        Delete
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="text-stone-300 hover:text-white hover:bg-stone-800 h-8 px-2"
      >
        <XMarkIcon className="h-4 w-4" />
      </Button>
    </div>
  );
};

// ============================================================================
// Inbox List
// ============================================================================

interface InboxListProps {
  table: Table<PackageRowData>;
  onRowClick: (id: string) => void;
}

const InboxList: React.FC<InboxListProps> = ({ table, onRowClick }) => (
  <div className="flex flex-col min-w-full">
    {/* Header */}
    <div className="sticky top-0 z-10 bg-white border-b border-stone-200 shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
      {table.getHeaderGroups().map((hg) => (
        <div key={hg.id} className="flex items-center h-9 px-0">
          {hg.headers.map((header) => {
            const canSort = header.column.getCanSort();
            const sorted = header.column.getIsSorted();

            return (
              <div
                key={header.id}
                role={canSort ? 'button' : undefined}
                tabIndex={canSort ? 0 : undefined}
                onClick={header.column.getToggleSortingHandler()}
                onKeyDown={(e) => {
                  if (canSort && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    header.column.getToggleSortingHandler()?.(e);
                  }
                }}
                className={cn(
                  'flex items-center gap-1 text-[11px] font-semibold text-stone-500 uppercase tracking-wide select-none transition-colors',
                  header.column.columnDef.meta?.className,
                  canSort &&
                    'cursor-pointer hover:text-stone-800 transition-colors',
                )}
                style={{ width: header.column.columnDef.meta?.width }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
                {sorted === 'asc' && (
                  <ChevronUpIcon className="h-3 w-3 text-stone-900" />
                )}
                {sorted === 'desc' && (
                  <ChevronDownIcon className="h-3 w-3 text-stone-900" />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>

    {/* Rows */}
    <div className="divide-y divide-stone-100">
      {table.getRowModel().rows.map((row) => (
        <InboxRow key={row.id} row={row} onRowClick={onRowClick} />
      ))}
    </div>
  </div>
);

// ============================================================================
// Inbox Row
// ============================================================================

interface InboxRowProps {
  row: Row<PackageRowData>;
  onRowClick: (id: string) => void;
}

const InboxRow: React.FC<InboxRowProps> = ({ row, onRowClick }) => {
  const pkg = row.original;
  const isSelected = row.getIsSelected();
  const isFailed = pkg.message?.transmission_status === 'failed';
  const isUnread = !pkg.is_transmitted;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onRowClick(pkg.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onRowClick(pkg.id);
        }
      }}
      data-state={isSelected ? 'selected' : undefined}
      className={cn(
        'group flex items-center h-12 cursor-pointer transition-colors duration-100 ease-out',
        // Base states
        'hover:bg-stone-50 hover:shadow-[inset_0_1px_0_rgba(0,0,0,0.02),inset_0_-1px_0_rgba(0,0,0,0.02)]',
        'active:bg-stone-100',
        // Selection state
        isSelected && 'bg-blue-50/60 hover:bg-blue-50',
        // Failed state
        isFailed && !isSelected && 'bg-red-50/40',
        // Unread state (subtle left border)
        isUnread && 'border-l-[3px] border-l-stone-900 pl-[13px]', // Compensate padding
        !isUnread && 'border-l-[3px] border-l-transparent pl-[13px]',
      )}
    >
      {row.getVisibleCells().map((cell) => (
        <div
          key={cell.id}
          className={cn(
            'flex items-center',
            cell.column.columnDef.meta?.className,
          )}
          style={{ width: cell.column.columnDef.meta?.width }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Loading Skeleton
// ============================================================================

const LoadingSkeleton: React.FC = () => (
  <div className="divide-y divide-stone-100">
    {Array.from({ length: 10 }).map((_, i) => (
      <div
        key={i}
        className="flex items-center h-12 px-4 animate-pulse"
        style={{ animationDelay: `${i * 30}ms` }}
      >
        <div className="w-11 flex-shrink-0">
          <div className="h-4 w-4 bg-stone-200 rounded" />
        </div>
        <div className="w-7 flex-shrink-0">
          <div className="h-2 w-2 bg-stone-200 rounded-full" />
        </div>
        <div className="w-20 flex-shrink-0">
          <div className="h-3 w-12 bg-stone-200 rounded" />
        </div>
        <div className="flex-1 px-3">
          <div className="h-4 w-48 bg-stone-100 rounded" />
        </div>
        <div className="w-36 hidden lg:block">
          <div className="h-3 w-20 bg-stone-100 rounded" />
        </div>
        <div className="w-24 hidden md:block">
          <div className="h-3 w-12 bg-stone-100 rounded" />
        </div>
        <div className="w-16 flex justify-end pr-4">
          <div className="h-3 w-10 bg-stone-100 rounded" />
        </div>
      </div>
    ))}
  </div>
);
